import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnrichedParentStopPlace } from '../../../../../../types';
import { showSuccessToast, submitFormByRef } from '../../../../../../utils';
import {
  InfoContainer,
  useInfoContainerControls,
} from '../../../../../common/InfoContainer';
import { terminalInfoContainerColors } from '../../Utils/terminalInfoContainerColors';
import { TerminalInfoSpotsForm } from './Edit/TerminalInfoSpotsForm';
import { useEditTerminalInfoSpots } from './Queries';
import { TerminalInfoSpotsHeaderButtons } from './TerminalInfoSpotsHeaderButtons';
import { TerminalInfoSpotsViewList } from './TerminalInfoSpotsViewList';
import {
  TerminalInfoSpotFormState,
  TerminalInfoSpotsSectionProps,
} from './Types';
import {
  defaultTerminalInfoSpotValues,
  getDefaultIntendedUser,
  getTerminalInfoSpotLabel,
  mapTerminalInfoSpotDataToFormState,
} from './utils';

function useNewInfoSpotFormDefaultValues(
  terminal: Readonly<EnrichedParentStopPlace>,
) {
  const terminalInfoSpotsFormDefaultValues = useMemo(
    () =>
      mapTerminalInfoSpotDataToFormState(
        {
          infoSpotLocations: [terminal.id ?? null],
          label: getTerminalInfoSpotLabel(terminal),
          intendedUser: getDefaultIntendedUser(terminal),
          ...defaultTerminalInfoSpotValues,
        },
        terminal,
      ),
    [terminal],
  );

  return { terminalInfoSpotsFormDefaultValues };
}

export const TerminalInfoSpotsSection: FC<TerminalInfoSpotsSectionProps> = ({
  terminal,
  infoSpots,
}) => {
  const { t } = useTranslation();

  const { saveTerminalInfoSpots, defaultErrorHandler } =
    useEditTerminalInfoSpots();

  const formRef = useRef<HTMLFormElement>(null);

  const { terminalInfoSpotsFormDefaultValues } =
    useNewInfoSpotFormDefaultValues(terminal);

  const infoContainerControls = useInfoContainerControls({
    isExpandable: true,
    isEditable: true,
    onSave: () => submitFormByRef(formRef),
  });
  const { isInEditMode } = infoContainerControls;

  const [latestAdded, setLatestAdded] = useState<string | undefined>();

  const onSubmit = async (state: TerminalInfoSpotFormState) => {
    try {
      const createdInfoSpotId = await saveTerminalInfoSpots({ state });
      setLatestAdded(createdInfoSpotId);

      showSuccessToast(t(($) => $.terminalDetails.editSuccess));
      infoContainerControls.setIsInEditMode(false);
    } catch (err) {
      defaultErrorHandler(err as Error);
    }
  };

  useEffect(() => {
    if (!infoContainerControls.isExpanded) {
      setLatestAdded(undefined);
    }
  }, [infoContainerControls.isExpanded]);

  const title = isInEditMode
    ? t(($) => $.terminalDetails.infoSpots.addTerminalInfoSpot)
    : t(($) => $.terminalDetails.infoSpots.title);

  return (
    <InfoContainer
      colors={terminalInfoContainerColors}
      bodyClassName="p-0"
      controls={infoContainerControls}
      headerButtons={
        <TerminalInfoSpotsHeaderButtons
          controls={infoContainerControls}
          testIdPrefix="TerminalInfoSpotsSection"
        />
      }
      title={title}
      ariaLabel={title}
      testIdPrefix="TerminalInfoSpotsSection"
    >
      {isInEditMode ? (
        <TerminalInfoSpotsForm
          defaultValues={terminalInfoSpotsFormDefaultValues}
          infoSpot={undefined}
          ref={formRef}
          terminal={terminal}
          onSubmit={onSubmit}
          onCancel={() => infoContainerControls.onCancel()}
          testIdPrefix="TerminalInfoSpotsSection"
        />
      ) : (
        <TerminalInfoSpotsViewList
          infoSpots={infoSpots}
          terminal={terminal}
          latestAdded={latestAdded}
        />
      )}
    </InfoContainer>
  );
};
